import { z } from 'zod';
import { MIN_PASSWORD_LENGTH } from '../../auth/schemas/auth.schemas.js';
import { membershipStatusSchema, userRoleSchema, userStatusSchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { paginatedSchema, paginationInputSchema, sortDirectionSchema } from '../../common/schemas/pagination.schemas.js';
/**
 * A row of `/settings/users`: the user account plus its membership in the caller's tenant. The two
 * statuses are separate on purpose — `status` disables the person inside this tenant, `userStatus`
 * disables the account everywhere — and both end the session on the next refresh (plan §2.3).
 */
export const tenantUserSchema = z.object({
    id: uuidSchema,
    email: z.string().email(),
    name: z.string().min(1),
    role: userRoleSchema,
    status: membershipStatusSchema,
    userStatus: userStatusSchema,
    mustChangePassword: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const listUsersSortBySchema = z.enum(['name', 'email', 'role', 'createdAt']);
export const listUsersInputSchema = paginationInputSchema.extend({
    sortBy: listUsersSortBySchema.default('name'),
    sortDirection: sortDirectionSchema.default('asc'),
    /** Free text over name and email. */
    q: z.string().trim().min(1).optional(),
    role: userRoleSchema.optional(),
    status: membershipStatusSchema.optional(),
});
export const listUsersOutputSchema = paginatedSchema(tenantUserSchema);
export const createUserInputSchema = z.object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
    name: z.string().trim().min(1, 'Name is required').max(255),
    role: userRoleSchema,
    password: z.string().min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters`),
});
export const updateUserRoleInputSchema = z.object({
    userId: uuidSchema,
    role: userRoleSchema,
});
export const setUserStatusInputSchema = z.object({
    userId: uuidSchema,
    status: membershipStatusSchema,
});
export const resetUserPasswordInputSchema = z.object({
    userId: uuidSchema,
});
/**
 * The generated password is returned once, so the admin can read it out to the person. It is never
 * stored in clear and never shown again; the user is forced through `/change-password` on first use.
 */
export const resetUserPasswordOutputSchema = z.object({
    user: tenantUserSchema,
    temporaryPassword: z.string().min(1),
});
//# sourceMappingURL=users.schemas.js.map